/**
 * Pieces shared by the enquiries page, its chat sheet and its two dialogs:
 * the channel pill, the form field and chip-radio wrappers, relative time,
 * and the budget/phone parsing both dialogs apply before they submit.
 */

import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { EnquiryChannel } from "@/features/realtor/services/realtor-crm-api.service";
import { parseRupeeInputToPaise } from "@/features/realtor/utils/listings-format";
import { cn } from "@/lib/utils";

/** Native <select> styled like the dashboard inputs. */
export const SELECT_CLASS =
	"h-10 w-full rounded-md border border-dashboard-border bg-dashboard-paper px-3 text-sm text-dashboard-text focus:outline-none focus:ring-2 focus:ring-dashboard-accent/30";

const CHANNEL_TONE: Record<EnquiryChannel, { bg: string; text: string }> = {
	whatsapp: {
		bg: "var(--color-dashboard-pill-done-bg)",
		text: "var(--color-dashboard-pill-done)",
	},
	phone_call: {
		bg: "var(--color-dashboard-pill-info-bg)",
		text: "var(--color-dashboard-pill-info)",
	},
	referral: {
		bg: "var(--color-dashboard-pill-active-bg)",
		text: "var(--color-dashboard-pill-active)",
	},
	walk_in: {
		bg: "var(--color-dashboard-pill-awaiting-bg)",
		text: "var(--color-dashboard-pill-awaiting)",
	},
	other: {
		bg: "var(--color-dashboard-pill-neutral-bg)",
		text: "var(--color-dashboard-pill-neutral)",
	},
};

export function ChannelPill({
	channel,
}: {
	channel: EnquiryChannel;
}): ReactElement {
	const { t } = useTranslation();
	const tone = CHANNEL_TONE[channel];
	return (
		<span
			className="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
			style={{ backgroundColor: tone.bg, color: tone.text }}
		>
			{t(`realtor.enquiries.channels.${channel}`)}
		</span>
	);
}

interface FormFieldProps {
	id: string;
	label: string;
	error?: string;
	hint?: string;
	children: ReactNode;
}

export function FormField({
	id,
	label,
	error,
	hint,
	children,
}: FormFieldProps): ReactElement {
	return (
		<div className="space-y-1.5">
			<label
				htmlFor={id}
				className="block text-sm font-medium text-dashboard-text"
			>
				{label}
			</label>
			{children}
			{error ? (
				<p
					id={`${id}-error`}
					className="text-xs text-dashboard-pill-risk"
					role="alert"
				>
					{error}
				</p>
			) : (
				hint && <p className="text-xs text-dashboard-muted">{hint}</p>
			)}
		</div>
	);
}

interface ChoiceChipsProps<T extends string> {
	name: string;
	legend: string;
	/** Undefined leaves every chip unchosen. */
	value: T | undefined;
	options: ReadonlyArray<{ value: T; label: string }>;
	onChange: (value: T) => void;
	/** Extra classes for each chip, e.g. a taller touch target. */
	chipClassName?: string;
}

/** A radio group drawn as chips; real radio inputs, so arrow keys work. */
export function ChoiceChips<T extends string>({
	name,
	legend,
	value,
	options,
	onChange,
	chipClassName,
}: ChoiceChipsProps<T>): ReactElement {
	return (
		<fieldset className="space-y-1.5">
			<legend className="mb-1.5 text-sm font-medium text-dashboard-text">
				{legend}
			</legend>
			<div className="flex flex-wrap gap-2">
				{options.map((option) => {
					const checked = option.value === value;
					return (
						<label
							key={option.value}
							className={cn(
								"inline-flex h-8 cursor-pointer items-center rounded-full border px-3 text-[13px] font-medium transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-dashboard-accent/40",
								checked
									? "border-transparent bg-dashboard-accent text-dashboard-on-fill"
									: "border-dashboard-border bg-dashboard-paper text-dashboard-muted-alt hover:text-dashboard-text",
								chipClassName,
							)}
						>
							<input
								type="radio"
								name={name}
								value={option.value}
								checked={checked}
								onChange={() => onChange(option.value)}
								className="sr-only"
							/>
							{option.label}
						</label>
					);
				})}
			</div>
		</fieldset>
	);
}

export type BudgetError = "budget" | "budgetRange";

export interface ParsedBudget {
	budgetMin: number | null;
	budgetMax: number | null;
	minError?: BudgetError;
	maxError?: BudgetError;
}

/**
 * Both budget inputs to paise. Empty is null; anything the rupee parser
 * rejects is an error on that field, and a max below the min is an error on
 * the max (the backend refuses the same range).
 */
export function parseBudgetRange(
	minInput: string,
	maxInput: string,
): ParsedBudget {
	const result: ParsedBudget = { budgetMin: null, budgetMax: null };
	if (minInput.trim()) {
		const paise = parseRupeeInputToPaise(minInput);
		if (paise == null) result.minError = "budget";
		else result.budgetMin = paise;
	}
	if (maxInput.trim()) {
		const paise = parseRupeeInputToPaise(maxInput);
		if (paise == null) result.maxError = "budget";
		else result.budgetMax = paise;
	}
	if (
		result.budgetMin != null &&
		result.budgetMax != null &&
		result.budgetMax < result.budgetMin
	) {
		result.maxError = "budgetRange";
	}
	return result;
}

/** The backend's rule for a phone: at most 20 characters, some of them digits. */
export function isAcceptablePhone(phone: string): boolean {
	return phone.length <= 20 && /\d/.test(phone);
}
